/**
 * Validation blocks, callouts, empty states and toasts.
 *
 * Errors and warnings are visibly different things: an error blocks the save, a warning describes
 * a state that is recordable but worth a second look. The error variant is `role="alert"`; the
 * warning variant deliberately is not, because a warning should not interrupt.
 *
 * The sentences rendered here are always the server's, verbatim. Nothing in this file composes
 * one (§6.2, ADR-0008).
 */

import type { ReactNode } from 'react';
import { plural } from '~/lib/format';
import { useToast } from '~/hooks/useToast';
import styles from './Feedback.module.css';

export interface ValidationMessagesProps {
  tone: 'error' | 'warn';
  title?: string;
  messages: readonly string[];
  /** A check is in flight; the last good result stays on screen beneath this note. */
  pending?: boolean;
}

export function ValidationMessages({ tone, title, messages, pending }: ValidationMessagesProps) {
  if (messages.length === 0) return null;
  const heading =
    title ??
    (tone === 'error'
      ? `${plural(messages.length, 'problem')} to fix before this can be saved`
      : 'Worth checking');

  return (
    <div
      className={[styles.vmsg, tone === 'error' ? styles.error : styles.warn].join(' ')}
      role={tone === 'error' ? 'alert' : undefined}
    >
      <p className={styles.vmsgTitle}>{heading}</p>
      <ul>
        {messages.map((message) => (
          <li key={message}>{message}</li>
        ))}
      </ul>
      {pending ? <p className={styles.pending}>Re-checking…</p> : null}
    </div>
  );
}

export interface CalloutProps {
  tone?: 'neutral' | 'warn' | 'issue' | 'quest';
  title: string;
  children: ReactNode;
  /** Set for a callout that reports a failure rather than a state. */
  alert?: boolean;
}

export function Callout({ tone = 'neutral', title, children, alert }: CalloutProps) {
  const toneClass =
    tone === 'warn'
      ? styles.calloutWarn
      : tone === 'issue'
        ? styles.calloutIssue
        : tone === 'quest'
          ? styles.calloutQuest
          : undefined;

  return (
    <div
      className={[styles.callout, toneClass].filter(Boolean).join(' ')}
      role={alert ? 'alert' : undefined}
    >
      <p className={styles.calloutTitle}>{title}</p>
      <div className={styles.calloutBody}>{children}</div>
    </div>
  );
}

export interface EmptyStateProps {
  /** `bare` inside a table wrapper, `card` for a standalone panel. */
  variant?: 'bare' | 'card';
  title?: string;
  children: ReactNode;
  actions?: ReactNode;
}

/**
 * These messages are long, specific, and explain why the state is empty and what would change
 * it. That editorial quality is the point of the component; it must not degrade into "No results".
 */
export function EmptyState({ variant = 'card', title, children, actions }: EmptyStateProps) {
  return (
    <div className={variant === 'bare' ? styles.empty : styles.emptyCard}>
      {title ? <h2>{title}</h2> : null}
      <div className={styles.emptyBody}>{children}</div>
      {actions ? <div className={styles.emptyActions}>{actions}</div> : null}
    </div>
  );
}

/** Confirmations are polite; failures interrupt. */
export function Toaster() {
  const { toasts, dismiss } = useToast();
  if (toasts.length === 0) return null;

  return (
    <div className={styles.toaster}>
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={[styles.toast, toast.tone === 'ok' ? styles.toastOk : styles.toastError].join(
            ' ',
          )}
          role={toast.tone === 'ok' ? 'status' : 'alert'}
        >
          <span>{toast.text}</span>
          <button
            type="button"
            className={styles.toastDismiss}
            onClick={() => dismiss(toast.id)}
            aria-label="Dismiss this message"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
